import type { SearchEntry } from '@/types/app'

// Bounded localStorage cache for the user's search/chat history (rendered in
// the /search/[id] sidebar). Capped to keep JSON.parse + stringify on every
// chat submission cheap — without a cap, power users accumulate megabytes
// over months and the hot-path becomes visibly slow.
const STORAGE_KEY = 'efficient:history'
const MAX_ENTRIES = 500

// localStorage access is wrapped in try/catch and fails open. Documented
// failure modes: SecurityError (Safari private browsing, third-party storage
// blocked, enterprise policies) and QuotaExceededError (origin storage full).
// Search history is best-effort UX — a storage error should never crash the
// chat submission flow.
export function readSearchHistory(): SearchEntry[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []

    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []

    const normalized: SearchEntry[] = parsed.map((item: SearchEntry) => ({
      ...item,
      timestamp: new Date(item.timestamp),
    }))

    // One-time cleanup: legacy unbounded blobs get trimmed on first read so
    // subsequent reads are fast.
    if (normalized.length > MAX_ENTRIES) {
      const trimmed = normalized.slice(0, MAX_ENTRIES)
      writeSearchHistory(trimmed)
      return trimmed
    }

    return normalized
  } catch {
    return []
  }
}

export function writeSearchHistory(entries: SearchEntry[]): void {
  if (typeof window === 'undefined') return
  const capped = entries.length > MAX_ENTRIES ? entries.slice(0, MAX_ENTRIES) : entries
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(capped))
  } catch {
    // QuotaExceededError or storage disabled — silently no-op.
  }
}

export function prependSearchHistoryEntry(entry: SearchEntry): SearchEntry[] {
  const current = readSearchHistory()
  const updated = [entry, ...current].slice(0, MAX_ENTRIES)
  writeSearchHistory(updated)
  return updated
}
